import React, { useState, useEffect, useRef } from "react";
import { getAdminReports } from "../../business/AdminBusiness";
import { getAllUsers, getUserById } from "../../business/UserBusiness";
import { Sidebar } from '../../components/Sidebar';
import { Modal } from '../../components/ui/Modal';
import { PlanBadge } from '../../components/ui/Badge';
import { PLAN_META, getEffectivePlan, getPlanSourceLabel } from '../../lib/planMeta';
import { TrendingUp, Users, Wallet, CalendarDays, Trophy, Filter, Mail, MessageCircle } from "lucide-react";
import toast from "react-hot-toast";

const QUICK_FILTERS = [
  { key: 'today', label: 'Hoje' },
  { key: 'week', label: 'Esta semana' },
  { key: '1month', label: '1 mês' },
  { key: '6months', label: '6 meses' },
  { key: 'year', label: 'Este ano' },
];

const StatCard = ({ icon: Icon, label, value, subtitle, iconBg, iconColor }) => (
  <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
    <div className={`mb-4 inline-flex rounded-lg p-2 ${iconBg}`}>
      <Icon className={`h-4 w-4 ${iconColor}`} />
    </div>
    <p className="text-sm text-gray-500">{label}</p>
    <p className="mt-1 text-2xl font-semibold text-gray-900">{value}</p>
    {subtitle && <p className="mt-2 text-xs text-gray-500">{subtitle}</p>}
  </div>
);

const RankingList = ({ icon: Icon, iconColor, title, isPeriodFiltered, items, renderValue, emptyLabel, onItemClick }) => (
  <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
    <div className="flex items-center mb-4">
      <Icon className={`h-5 w-5 mr-2 ${iconColor}`} />
      <h3 className="text-base font-semibold text-gray-900">{title}</h3>
      {isPeriodFiltered && <span className="ml-2 text-xs text-accent font-medium">(período)</span>}
    </div>
    {items.length > 0 ? (
      <div className="space-y-1">
        {items.map((item, index) => (
          <div key={item._id || index} onClick={() => onItemClick(item)} className="flex items-center justify-between p-3 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
            <div className="flex items-center min-w-0">
              <span className={`text-sm font-bold mr-3 w-6 text-center ${index === 0 ? 'text-yellow-500' : index === 1 ? 'text-gray-400' : index === 2 ? 'text-amber-600' : 'text-gray-400'}`}>
                #{index + 1}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{item.username || 'Sem nome'}</p>
                {item.businessName && <p className="text-xs text-gray-500 truncate">{item.businessName}</p>}
              </div>
            </div>
            <div className="text-right ml-3 flex-shrink-0">{renderValue(item)}</div>
          </div>
        ))}
      </div>
    ) : (
      <p className="text-gray-400 text-center py-8 text-sm">{emptyLabel}</p>
    )}
  </div>
);

export const Reports = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [reportData, setReportData] = useState({
    usersByPlan: { free: 0, start: 0, pro: 0, studio: 0, business: 0, enterprise: 0 },
    totalTransacted: 0,
    totalTransactions: 0,
    totalAppointments: 0,
    topTransactors: [],
    topByAppointments: [],
  });
  const [totalUsers, setTotalUsers] = useState(0);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [isLoadingUserDetails, setIsLoadingUserDetails] = useState(false);
  const filterRef = useRef(null);

  const adminName = localStorage.getItem("@Auth:name");

  const getInitials = (username) => {
    if (!username) return '?';
    const names = username.trim().split(' ');
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  };

  const getAvatarColor = (username) => {
    if (!username) return 'bg-gray-500';
    const colors = ['bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500'];
    return colors[username.charCodeAt(0) % colors.length];
  };

  const getRoleLabel = (role) => {
    const map = { admin: 'Administrador', user: 'Usuário', patner: 'Parceiro', trial: 'Trial' };
    return map[role] || role;
  };

  const getRoleColor = (role) => {
    const map = { admin: 'bg-red-100 text-red-700', user: 'bg-blue-100 text-blue-700', patner: 'bg-green-100 text-green-700', trial: 'bg-amber-100 text-amber-700' };
    return map[role] || 'bg-gray-100 text-gray-700';
  };

  const formatCreatedAt = (createdAt) => {
    if (!createdAt) return '—';
    return new Date(createdAt).toLocaleDateString('pt-BR', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handlePersonClick = async (item) => {
    if (!item._id) return;
    try {
      setIsLoadingUserDetails(true);
      setSelectedPerson({ username: item.username, businessName: item.businessName, _id: item._id });
      const response = await getUserById(item._id);
      if (response?.data?.user) {
        setSelectedPerson({ ...item, ...response.data.user, clients: response.data.user.clients || [], employees: response.data.user.employees || [] });
      }
    } catch (error) {
      toast.error('Erro ao carregar detalhes do usuário');
    } finally {
      setIsLoadingUserDetails(false);
    }
  };

  const toInputDate = (d) => d.toISOString().split('T')[0];

  const fetchData = async (sd = startDate, ed = endDate) => {
    try {
      setIsLoading(true);
      const params = {};
      if (sd) params.start_date = sd;
      if (ed) params.end_date = ed;
      const [reportsRes, usersRes] = await Promise.all([
        getAdminReports(params),
        getAllUsers(),
      ]);
      setReportData(reportsRes?.data || reportData);
      setTotalUsers((usersRes?.data || []).length);
    } catch (error) {
      console.error('Erro ao buscar relatórios:', error);
      toast.error('Erro ao carregar relatórios');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setFilterOpen(false);
      }
    };
    if (filterOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [filterOpen]);

  const applyQuickFilter = (type) => {
    const today = new Date();
    let start = new Date(today);
    if (type === 'week') {
      const day = today.getDay();
      start.setDate(today.getDate() + (day === 0 ? -6 : 1 - day));
    } else if (type === '1month') {
      start.setMonth(today.getMonth() - 1);
    } else if (type === '6months') {
      start.setMonth(today.getMonth() - 6);
    } else if (type === 'year') {
      start = new Date(today.getFullYear(), 0, 1);
    }
    const sd = toInputDate(start);
    const ed = toInputDate(today);
    setStartDate(sd);
    setEndDate(ed);
    fetchData(sd, ed);
    setFilterOpen(false);
  };

  const handleFilter = (e) => {
    e.preventDefault();
    fetchData();
    setFilterOpen(false);
  };

  const handleClearFilter = () => {
    setStartDate('');
    setEndDate('');
    fetchData('', '');
    setFilterOpen(false);
  };

  const formatCurrency = (amount) =>
    Number(amount || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const { usersByPlan, totalTransacted, totalTransactions, totalAppointments, topTransactors, topByAppointments } = reportData;
  const totalUsersForPlan = Object.values(usersByPlan).reduce((a, b) => a + b, 0);
  const isPeriodFiltered = Boolean(startDate || endDate);

  return (
    <>
      <div className="flex flex-col lg:flex-row bg-gray-50 min-h-screen">
        <Sidebar />
        <div className="flex-1 min-h-screen">
          <div className="max-w-7xl w-full mx-auto p-3 sm:p-6 space-y-6">

            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Olá, {adminName}</h1>
                <p className="text-sm text-gray-500">Relatórios — Visão geral do sistema</p>
              </div>

              <div className="relative" ref={filterRef}>
                <button
                  onClick={() => setFilterOpen(v => !v)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
                    isPeriodFiltered
                      ? 'bg-accent text-white border-accent'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <Filter size={13} />
                  {isPeriodFiltered ? 'Filtro ativo' : 'Filtrar'}
                </button>

                {filterOpen && (
                  <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-lg border border-gray-200 p-4 z-20">
                    <div className="flex gap-1.5 flex-wrap mb-3">
                      {QUICK_FILTERS.map(f => (
                        <button
                          key={f.key}
                          onClick={() => applyQuickFilter(f.key)}
                          className="px-2.5 py-1 rounded-full bg-gray-100 hover:bg-accent-chip hover:text-accent text-gray-700 text-xs font-medium transition-colors"
                        >
                          {f.label}
                        </button>
                      ))}
                    </div>

                    <div className="border-t border-gray-100 pt-3">
                      <form onSubmit={handleFilter} className="space-y-2">
                        <div>
                          <label className="text-xs text-gray-500 mb-0.5 block">Data inicial</label>
                          <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full rounded-md border border-gray-300 py-1.5 px-3 text-gray-900 text-sm focus:ring-2 focus:ring-accent focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 mb-0.5 block">Data final</label>
                          <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full rounded-md border border-gray-300 py-1.5 px-3 text-gray-900 text-sm focus:ring-2 focus:ring-accent focus:outline-none"
                          />
                        </div>
                        <div className="flex gap-2 pt-1">
                          <button
                            type="submit"
                            disabled={isLoading}
                            className="flex-1 bg-accent hover:bg-accent-hover text-white font-semibold py-1.5 rounded-md text-sm transition-colors disabled:opacity-50"
                          >
                            {isLoading ? 'Buscando...' : 'Buscar'}
                          </button>
                          {isPeriodFiltered && (
                            <button
                              type="button"
                              onClick={handleClearFilter}
                              className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-md text-sm transition-colors"
                            >
                              Limpar
                            </button>
                          )}
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {isLoading ? (
              <div className="flex justify-center items-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent mx-auto"></div>
                  <p className="mt-4 text-sm text-gray-500">Carregando relatórios...</p>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard
                    icon={Wallet} iconBg="bg-green-50" iconColor="text-green-600"
                    label={`Total transacionado${isPeriodFiltered ? ' (período)' : ''}`}
                    value={formatCurrency(totalTransacted)}
                    subtitle={`${totalTransactions} entradas`}
                  />
                  <StatCard
                    icon={CalendarDays} iconBg="bg-accent-chip" iconColor="text-accent"
                    label={`Agendamentos${isPeriodFiltered ? ' (período)' : ''}`}
                    value={totalAppointments}
                    subtitle="agendamentos cadastrados"
                  />
                  <StatCard
                    icon={Users} iconBg="bg-blue-50" iconColor="text-blue-600"
                    label="Total de usuários"
                    value={totalUsers}
                    subtitle="contas cadastradas"
                  />
                  <StatCard
                    icon={TrendingUp} iconBg="bg-purple-50" iconColor="text-purple-600"
                    label="Ticket médio"
                    value={formatCurrency(totalTransactions > 0 ? totalTransacted / totalTransactions : 0)}
                    subtitle="por entrada"
                  />
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                  <div className="flex items-center mb-4">
                    <Users className="h-5 w-5 text-accent mr-2" />
                    <h3 className="text-base font-semibold text-gray-900">Distribuição por plano</h3>
                    <span className="ml-auto text-sm text-gray-400">{totalUsersForPlan} usuários</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                    {Object.keys(PLAN_META).map((plan) => {
                      const meta = PLAN_META[plan];
                      const count = usersByPlan[plan] || 0;
                      const pct = totalUsersForPlan > 0 ? Math.round((count / totalUsersForPlan) * 100) : 0;
                      return (
                        <div key={plan} className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <PlanBadge plan={plan} meta={PLAN_META} />
                            <span className="text-sm font-bold text-gray-700">{count}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="h-2 rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: meta.color }} />
                          </div>
                          <p className="text-xs text-gray-500 mt-1 text-right">{pct}%</p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <RankingList
                    icon={Trophy} iconColor="text-yellow-500" title="Maior faturamento"
                    isPeriodFiltered={isPeriodFiltered} items={topTransactors} onItemClick={handlePersonClick}
                    emptyLabel="Nenhuma entrada encontrada"
                    renderValue={(item) => (
                      <>
                        <p className="text-sm font-bold text-green-600">{formatCurrency(item.totalValue)}</p>
                        <p className="text-xs text-gray-400">{item.count} entradas</p>
                      </>
                    )}
                  />
                  <RankingList
                    icon={CalendarDays} iconColor="text-accent" title="Top agendamentos"
                    isPeriodFiltered={isPeriodFiltered} items={topByAppointments} onItemClick={handlePersonClick}
                    emptyLabel="Nenhum agendamento encontrado"
                    renderValue={(item) => (
                      <>
                        <p className="text-sm font-bold text-accent">{item.count}</p>
                        <p className="text-xs text-gray-400">agendamentos</p>
                      </>
                    )}
                  />
                </div>
              </>
            )}
          </div>

          <Modal title="Detalhes do Usuário" isOpen={!!selectedPerson} onClose={() => setSelectedPerson(null)}>
            {isLoadingUserDetails ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
                <span className="ml-3 text-sm text-gray-500">Carregando detalhes...</span>
              </div>
            ) : selectedPerson && (
              <>
                <div className="flex items-center mb-6">
                  <div className={`h-14 w-14 rounded-full flex items-center justify-center text-white font-semibold text-xl ${getAvatarColor(selectedPerson?.username)}`}>
                    {getInitials(selectedPerson?.username)}
                  </div>
                  <div className="ml-4">
                    <h3 className="text-base font-semibold text-gray-900">{selectedPerson?.username}</h3>
                    {selectedPerson?.role && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleColor(selectedPerson?.role)}`}>
                        {getRoleLabel(selectedPerson?.role)}
                      </span>
                    )}
                  </div>
                </div>

                <div className="mb-5">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Informações de contato</h4>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Email:</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 break-all">{selectedPerson?.email || <span className="text-gray-400">—</span>}</span>
                        {selectedPerson?.email && (
                          <a
                            href={`mailto:${selectedPerson.email}`}
                            className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                            title="Enviar e-mail"
                          >
                            <Mail className="h-3.5 w-3.5" />
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Telefone:</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">{selectedPerson?.phone || <span className="text-gray-400">—</span>}</span>
                        {selectedPerson?.phone && (
                          <a
                            href={`https://wa.me/${String(selectedPerson.phone).replace(/\D/g, "")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-green-600"
                            title="Abrir WhatsApp"
                          >
                            <MessageCircle className="h-3.5 w-3.5" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-5">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Informações do negócio</h4>
                  <div className="space-y-1.5">
                    <div className="flex justify-between"><span className="text-sm text-gray-500">Nome do negócio:</span><span className="text-sm font-medium text-gray-900">{selectedPerson?.businessName || <span className="text-gray-400">Não informado</span>}</span></div>
                    <div className="flex justify-between"><span className="text-sm text-gray-500">Receita média:</span><span className="text-sm font-medium text-gray-900">{selectedPerson?.averageRevenue != null ? formatCurrency(selectedPerson.averageRevenue) : <span className="text-gray-400">Não informado</span>}</span></div>
                    <div className="flex justify-between"><span className="text-sm text-gray-500">Clientes:</span><span className="text-sm font-medium text-gray-900">{selectedPerson?.clients?.length || 0}</span></div>
                    <div className="flex justify-between"><span className="text-sm text-gray-500">Funcionários:</span><span className="text-sm font-medium text-gray-900">{selectedPerson?.employees?.length || 0}</span></div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Status e sistema</h4>
                  <div className="space-y-1.5">
                    <div className="flex justify-between items-center"><span className="text-sm text-gray-500">Plano:</span><div className="flex flex-col items-end gap-0.5"><PlanBadge plan={getEffectivePlan(selectedPerson)} meta={PLAN_META} /><span className="text-[11px] text-gray-400">{getPlanSourceLabel(selectedPerson)}</span></div></div>
                    <div className="flex justify-between"><span className="text-sm text-gray-500">Assinatura real:</span><span className={`text-sm font-medium ${selectedPerson?.subscription === 'active' ? 'text-green-600' : 'text-red-600'}`}>{selectedPerson?.subscription === 'active' ? 'Ativo' : 'Inativo'}</span></div>
                    <div className="flex justify-between"><span className="text-sm text-gray-500">Criado em:</span><span className="text-sm font-medium text-gray-900">{formatCreatedAt(selectedPerson?.createdAt)}</span></div>
                    <div className="flex justify-between"><span className="text-sm text-gray-500">ID:</span><span className="text-sm font-medium text-gray-400 break-all">{selectedPerson?._id}</span></div>
                  </div>
                </div>
              </>
            )}
          </Modal>
        </div>
      </div>
    </>
  );
};
